import "../k";
import { LEVELS, loadLevelSprites } from "../data";
import { addPic } from "../things/pic";
import { addVote } from "../things/vote";
import { addBadge } from "../things/badge";
import { addLabel } from "../things/label";
import { addButton } from "../things/button";
import { goTransition } from "../util/goTransition";
import { addVideoPlayer } from "../things/videoPlayer";
import { addAudioPlayer } from "../things/audioPlayer";

scene("quiz", (levelIndex: number) => {
  loadLevelSprites(levelIndex);
  let currentQuestionIndex = 0;
  let score = 0;

  let questionNumberText, scoreText;

  const cleanupQuestion = () => {
    destroyAll("questionText");
    destroyAll("imageChoice");
    destroyAll("feedback");
    destroyAll("nextButton");
    destroyAll("explanationText");
  };

  const setupQuizUI = () => {
    const backButton = addButton("back", [pos(100, 100), "backButton"]);
    backButton.onClick(() => {
      goTransition("levelSelect");
    });

    questionNumberText = add([
      text(
        `Question ${currentQuestionIndex + 1}/${
          LEVELS[levelIndex].questions.length
        }`,
        {
          size: 32,
          font: "rumeur",
        }
      ),
      anchor("left"),
      color(0, 0, 0),
      pos(228, 64),
    ]);

    scoreText = add([
      text(`Score: ${score}/${LEVELS[levelIndex].questions.length}`, {
        size: 32,
        font: "rumeur",
      }),
      anchor("right"),
      color(0, 0, 0),
      pos(width() - 228, 64),
    ]);
  };

  const updateScoreAndQuestionText = () => {
    questionNumberText.text = `Question ${currentQuestionIndex + 1}/${
      LEVELS[levelIndex].questions.length
    }`;
    scoreText.text = `Score: ${score}/${LEVELS[levelIndex].questions.length}`;
  };

  const handleAnswer = (isCorrect: boolean, cleanupSpecifics: () => void) => {
    const question = LEVELS[levelIndex].questions[currentQuestionIndex]; // Get current question
    if (isCorrect) {
      score++;
      updateScoreAndQuestionText();
      play("confirm", { volume: 0.5 });
    } else {
      play("error", { volume: 0.3 });
    }

    if (question.explanation) {
      add([
        text(question.explanation, {
          size: 24,
          font: "rumeur",
          width: width() * 0.7, // Limit width to prevent overflow
        }),
        pos(width() / 2, height() * 0.77), // Position below badge
        anchor("center"),
        color(0, 0, 0),
        "explanationText", // Tag for cleanup
      ]);
    }

    addBadge(isCorrect, [pos(width() / 2, (height() * 2.5) / 4), "feedback"]);

    cleanupSpecifics();
    displayNextButton();
  };

  const displayTwoImageQuestion = (question: TwoImageQuestion) => {
    const isRealLeft = Math.random() < 0.5;
    const left = pos(418, 355);
    const right = pos(862, 355);

    const image1 = addPic(question, isRealLeft, [left, "imageChoice"]);
    const image2 = addPic(question, !isRealLeft, [right, "imageChoice"]);

    const twoImageCleanup = () => {
      image1.isEnabled = false;
      image2.isEnabled = false;
      image1.isInspectable = false;
      image2.isInspectable = false;
      destroy(promptText);
      addLabel(isRealLeft, [left, "feedback"]);
      addLabel(!isRealLeft, [right, "feedback"]);
    };

    image1.onClick(() => {
      if (image1.isEnabled) {
        handleAnswer(!isRealLeft, twoImageCleanup);
      }
    });
    image2.onClick(() => {
      if (image2.isEnabled) {
        handleAnswer(isRealLeft, twoImageCleanup);
      }
    });

    const promptText = add([
      text(`Click on the AI-generated image!`, {
        size: 24,
        font: "rumeur",
      }),
      anchor("center"),
      color(0, 0, 0),
      pos(width() / 2, 720),
      "questionText",
    ]);
  };

  const displaySingleImageQuestion = (question: SingleImageQuestion) => {
    const imagePos = pos(width() / 2, 355);
    const image = addPic(question.image, true, [imagePos, "imageChoice"]);
    image.isEnabled = false;

    const realButton = addButton("realbutton", [
      pos(width() / 2 - 102, 670),
      anchor("center"),
      "singleImageButton",
    ]);
    realButton.add([
      text("Real", { size: 40, font: "rumeur" }),
      anchor("center"),
      color(0, 0, 0),
      pos(0, 35),
    ]);

    const aiButton = addButton("aibutton", [
      pos(width() / 2 + 102, 670),
      anchor("center"),
      "singleImageButton",
    ]);
    aiButton.add([
      text("AI", { size: 40, font: "rumeur" }),
      anchor("center"),
      color(0, 0, 0),
      pos(0, 35),
    ]);

    const oneImageCleanup = () => {
      image.isEnabled = false;
      image.isInspectable = false;
      destroyAll("singleImageButton");
      addLabel(question.isReal, [imagePos, "feedback"]);
    };

    realButton.onClick(() => handleAnswer(question.isReal, oneImageCleanup));
    aiButton.onClick(() => handleAnswer(!question.isReal, oneImageCleanup));
  };

  const displayVideoQuestion = async (question: VideoQuestion) => {
    const isRealLeft = Math.random() < 0.5;
    const left = pos(418, 355);
    const right = pos(862, 355);

    // Load both videos in parallel
    const [video1, video2] = await Promise.all([
      addVideoPlayer(isRealLeft ? question.real : question.ai, isRealLeft, [left, "imageChoice"]),
      addVideoPlayer(!isRealLeft ? question.real : question.ai, !isRealLeft, [right, "imageChoice"]),
    ]);

    const videoCleanup = () => {
      video1.isEnabled = false;
      video2.isEnabled = false;
      video1.stop();
      video2.stop();
      destroy(promptText);
      addLabel(isRealLeft, [left, "feedback"]);
      addLabel(!isRealLeft, [right, "feedback"]);
    };

    video1.onClick(() => {
      if (video1.isEnabled) {
        handleAnswer(!isRealLeft, videoCleanup);
      }
    });
    video2.onClick(() => {
      if (video2.isEnabled) {
        handleAnswer(isRealLeft, videoCleanup);
      }
    });

    const promptText = add([
      text(`Click on the AI-generated video!`, {
        size: 24,
        font: "rumeur",
      }),
      anchor("center"),
      color(0, 0, 0),
      pos(width() / 2, 720),
      "questionText",
    ]);
  };

  const displayAudioQuestion = (question: AudioQuestion) => {
    const isRealLeft = Math.random() < 0.5;
    const left = pos(418, 355);
    const right = pos(862, 355);

    const audio1 = addAudioPlayer(isRealLeft ? question.real : question.ai, isRealLeft, [left, "imageChoice"]);
    const audio2 = addAudioPlayer(!isRealLeft ? question.real : question.ai, !isRealLeft, [right, "imageChoice"]);

    const audioCleanup = () => {
      audio1.isEnabled = false;
      audio2.isEnabled = false;
      audio1.stop();
      audio2.stop();
      destroy(promptText);
      addLabel(isRealLeft, [left, "feedback"]);
      addLabel(!isRealLeft, [right, "feedback"]);
    };

    audio1.onClick(() => {
      if (audio1.isEnabled) {
        handleAnswer(!isRealLeft, audioCleanup);
      }
    });
    audio2.onClick(() => {
      if (audio2.isEnabled) {
        handleAnswer(isRealLeft, audioCleanup);
      }
    });

    const promptText = add([
      text(`Click on the AI-generated audio!`, {
        size: 24,
        font: "rumeur",
      }),
      anchor("center"),
      color(0, 0, 0),
      pos(width() / 2, 720),
      "questionText",
    ]);
  };

  const displayNextButton = () => {
    const nextButton = addButton("button", [
      pos(width() / 2, height() - 100),
      area(),
      "nextButton",
      { textContents: "Next" },
    ]);

    nextButton.onClick(() => {
      currentQuestionIndex++;
      cleanupQuestion();
      displayQuestion();
    });
  };

  const displayQuestion = async () => {
    if (currentQuestionIndex >= LEVELS[levelIndex].questions.length) {
      goTransition("end", score, levelIndex);
      return;
    }

    const question = LEVELS[levelIndex].questions[currentQuestionIndex];
    updateScoreAndQuestionText(); // Update text for new question

    if (question.type === "two-image") {
      displayTwoImageQuestion(question as TwoImageQuestion);
    } else if (question.type === "single-image") {
      displaySingleImageQuestion(question as SingleImageQuestion);
    } else if (question.type === "video") {
      await displayVideoQuestion(question as VideoQuestion);
    } else if (question.type === "audio") {
      displayAudioQuestion(question as AudioQuestion);
    }
  };

  setupQuizUI(); // Setup static UI elements once

  displayQuestion();
});
